import { useRef, useEffect, useCallback } from 'react';
import type { MutableRefObject } from 'react';
import type { BryntumSchedulerPro } from '@bryntum/schedulerpro-react-thin';
import { getDateRangeForView } from './utils';
import type { ViewType } from './types';

type SchedulerRef = MutableRefObject<BryntumSchedulerPro | null>;

type Props = {
  view: ViewType;
  currentDate: Date;
  onViewChange: (view: ViewType) => void;
  onDateChange: (date: Date) => void;
};

type NavigationHandlers = {
  handleViewChange: (newView: ViewType) => void;
  handleNavigatePrevious: () => void;
  handleNavigateNext: () => void;
  handleNavigateToday: () => void;
};

export function useSchedulerNavigation(
  schedulerRef: SchedulerRef,
  { view, currentDate, onViewChange, onDateChange }: Props,
): NavigationHandlers {
  // Stash the target time span here; the effect below applies it after React
  // has committed the new viewPreset prop to Bryntum (useEffect fires post-commit).
  const pendingTimeSpanRef = useRef<{ startDate: Date; endDate: Date } | null>(
    null,
  );
  useEffect(() => {
    if (!pendingTimeSpanRef.current) return;
    const { startDate: s, endDate: e } = pendingTimeSpanRef.current;
    schedulerRef.current?.instance?.setTimeSpan(s, e);
    pendingTimeSpanRef.current = null;
  }, [view, schedulerRef]);

  const handleViewChange = useCallback(
    (newView: ViewType) => {
      pendingTimeSpanRef.current = getDateRangeForView(currentDate, newView);
      onViewChange(newView);
    },
    [onViewChange, currentDate],
  );

  // After each Bryntum shift call, read the new date range and sync state/URL.
  const syncDateFromScheduler = useCallback(() => {
    const scheduler = schedulerRef.current?.instance;
    if (!scheduler) return;
    const s = scheduler.startDate;
    const e = scheduler.endDate;
    onDateChange(new Date((s.getTime() + e.getTime()) / 2));
  }, [onDateChange, schedulerRef]);

  const handleNavigatePrevious = useCallback(() => {
    schedulerRef.current?.instance?.shiftPrevious();
    syncDateFromScheduler();
  }, [syncDateFromScheduler, schedulerRef]);

  const handleNavigateNext = useCallback(() => {
    schedulerRef.current?.instance?.shiftNext();
    syncDateFromScheduler();
  }, [syncDateFromScheduler, schedulerRef]);

  const handleNavigateToday = useCallback(() => {
    const today = new Date();
    const { startDate: s, endDate: e } = getDateRangeForView(today, view);
    schedulerRef.current?.instance?.setTimeSpan(s, e);
    onDateChange(today);
  }, [view, onDateChange, schedulerRef]);

  return {
    handleViewChange,
    handleNavigatePrevious,
    handleNavigateNext,
    handleNavigateToday,
  };
}
